import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiHelpers from '../../utils/apiHelpers';
import cardUtils from '../../utils/cardUtils';
import formatter from '../../utils/formatter';
import CardList from './CardList';
import CardBulkList from '../findBulk/CardBulkList';
import CardFilters from '../card/CardFilters';
import SelectFunctionalTags from '../common/selects/SelectFunctionalTags';
import CardsNotFoundList from '../card/CardsNotFoundList';
import Loader from '../common/Loader';
import Button from '../common/Button';
import { useDebounce } from 'use-debounce';
import Input from '../common/Input';
import Alert from '../common/Alert';
import TotalAmount from '../common/TotalAmount';

const hasAdvancedFilters = (advanced, functionalTags) => {
  const adv = advanced || {};
  return Boolean(
    (adv.colors && adv.colors.length) ||
      (adv.cmc !== null && adv.cmc !== undefined) ||
      (functionalTags && functionalTags.length),
  );
};

const FindCard = () => {
  // Lets other sections deep-link a prefilled search, e.g. the Deck Builder's
  // "Encontrar en tiendas" button.
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [cardsNotFound, setCardsNotFound] = useState([]);
  const [sortedCards, setSortedCards] = useState([]);
  const [cardName, setCardName] = useState(
    () => searchParams.get('name') || '',
  );
  // { where, advanced } — one object from the unified CardFilters.
  const [filters, setFilters] = useState({});
  const [functionalTags, setFunctionalTags] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  // Total matches for the current advanced search, or null for local searches
  // (/card returns a plain array with no count).
  const [totalCards, setTotalCards] = useState(null);
  const [showSorted, setShowSorted] = useState(false);
  const [showCards, setShowCards] = useState(true);
  const [debouncedCardName] = useDebounce(cardName, 1000);
  const [errorMessage, setErrorMessage] = useState('');

  const searchLocal = useCallback(
    async card_name => {
      // Only `where` goes to /card — `advanced` is applied by the Scryfall path
      // below and would just bloat the query string here.
      const reqFilters = {
        where: { ...(filters.where || {}), name: card_name.trim() },
        limit: 1000,
      };
      const response = await apiHelpers.getCards(reqFilters);
      setCardsNotFound([]);
      setHasMore(false);
      setTotalCards(null);
      return response.data.length > 0 ? response.data : [];
    },
    [filters],
  );

  const searchAdvanced = useCallback(
    async (card_name, targetPage) => {
      const where = filters.where || {};
      const advanced = filters.advanced || {};
      const payload = {
        name: card_name.trim() || undefined,
        colors: advanced.colors || undefined,
        colorMode: advanced.colorMode || undefined,
        cmc:
          advanced.cmc !== null && advanced.cmc !== undefined
            ? advanced.cmc
            : undefined,
        cmcComparator: advanced.cmcComparator || undefined,
        type: where.types || undefined,
        functionalTags: functionalTags.length ? functionalTags : undefined,
        conditionRank: where.conditionRank || undefined,
        language: where.language || undefined,
        priceRank: where.priceRank || undefined,
        minPrice: where.minPrice || undefined,
        maxPrice: where.maxPrice || undefined,
        stores: where.stores || undefined,
        page: targetPage,
      };
      const response = await apiHelpers.searchDeckBuilderCards(payload);
      setCardsNotFound(response.data.cardsNotFound || []);
      setHasMore(response.data.hasMore);
      setTotalCards(
        typeof response.data.totalCards === 'number'
          ? response.data.totalCards
          : null,
      );
      setPage(targetPage);
      return response.data.cards || [];
    },
    [filters, functionalTags],
  );

  const runSearch = useCallback(
    async (card_name, append) => {
      setLoading(true);
      setErrorMessage('');
      try {
        const newCards = hasAdvancedFilters(filters.advanced, functionalTags)
          ? await searchAdvanced(card_name, append ? page + 1 : 1)
          : await searchLocal(card_name);
        setCards(prevCards => {
          const merged = append ? prevCards.concat(newCards) : newCards;
          return merged.filter(
            (card, index, self) =>
              index === self.findIndex(c => c.id === card.id),
          );
        });
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);

        if (error.response) {
          const { status, data } = error.response;
          if (status === 400 && data?.message) {
            setErrorMessage(data.message);
          } else if ([400, 422, 204].includes(status)) {
            setErrorMessage('Error al cargar datos de las cartas');
          } else if (error.request) {
            setErrorMessage('Sin respuesta del Servidor');
          } else {
            setErrorMessage(error.message);
          }
        } else {
          setErrorMessage('Error de conexión con el servidor');
        }
      }
    },
    [filters, functionalTags, page, searchAdvanced, searchLocal],
  );

  useEffect(() => {
    // CardFilters reports its (empty) filters in a mount effect, one render after
    // ours. With a prefilled ?name= this effect would otherwise fire first and
    // search with no `where` object at all.
    if (!filters.where) return;
    if (
      debouncedCardName.length >= 4 ||
      hasAdvancedFilters(filters.advanced, functionalTags)
    ) {
      runSearch(debouncedCardName, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCardName, filters, functionalTags]);

  useEffect(() => {
    if (cards.length) {
      setSortedCards(
        cardUtils.groupCardsByStore(
          cards.filter(card => card.cardPrices && card.cardPrices.length > 0),
        ),
      );
    } else {
      setSortedCards([]);
    }
  }, [cards]);

  const handleChange = e => {
    setCardName(e.target.value);
  };

  const handleChangeFilters = filters => {
    setFilters(filters);
  };

  const handleFunctionalTagsChange = event => {
    setFunctionalTags(event.target.value);
  };

  // CardFilters clears its own state, including colors and mana value; the tag
  // selector lives outside it, so it is cleared here.
  const handleFiltersReset = () => {
    setFunctionalTags([]);
  };

  const loadMore = () => {
    runSearch(debouncedCardName, true);
  };

  return (
    <div className="flex flex-col gap-y-4">
      <h1 className="text-2xl text-center">
        <i className="fas fa-search mr-2"></i>
        Buscar carta
      </h1>
      <h4>
        Buscá una carta por nombre y compará su precio entre todas las tiendas
        que seguimos. Usá los filtros para acotar por condición, idioma, tienda,
        rango de precio, color, valor de maná o etiquetas funcionales, y agregá
        lo que encuentres a tu wishlist, tu colección o tu carrito.
      </h4>

      <div className="flex flex-col gap-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="w-full sm:w-1/2">
            <Input
              name="card_name"
              placeholder='Ingrese el nombre de una carta. Por ejemplo, "Llanowar elves"'
              value={cardName}
              onChange={handleChange}
            />
          </div>
          <div className="w-full sm:w-1/2">
            <SelectFunctionalTags
              value={functionalTags}
              name="functionalTags"
              placeholder="Etiquetas funcionales (ej: rampa, remoción)"
              onChange={handleFunctionalTagsChange}
            />
          </div>
        </div>

        <CardFilters
          loadData={handleChangeFilters}
          onReset={handleFiltersReset}
          fields={[
            'condition',
            'priceRank',
            'language',
            'price',
            'types',
            'stores',
            'colors',
            'cmc',
          ]}
        />

        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            className="w-full sm:w-1/3"
            iconClass="fas fa-id-card"
            onClick={() => {
              setShowSorted(false);
              setShowCards(true);
            }}
            disabled={loading}>
            Ver por carta
          </Button>
          <Button
            className="w-full sm:w-1/3"
            iconClass="fas fa-store"
            onClick={() => {
              setShowSorted(true);
              setShowCards(true);
            }}
            disabled={loading}>
            Ver por tienda (cartas)
          </Button>
          <Button
            className="w-full sm:w-1/3"
            iconClass="fas fa-list-alt"
            onClick={() => {
              setShowSorted(true);
              setShowCards(false);
            }}
            disabled={loading}>
            Ver por tienda (lista)
          </Button>
        </div>

        {loading && <Loader />}
        {!loading && !showSorted && (
          <>
            <Alert>{errorMessage}</Alert>
            <CardList query={cardName} cards={cards} showCards={showCards} />
          </>
        )}
        {!loading && showSorted && (
          <>
            <Alert>{errorMessage}</Alert>
            <TotalAmount cards={cards} />
            {sortedCards.map(cardsByStore => (
              <CardBulkList
                key={cardsByStore.store}
                store={cardsByStore.store}
                cards={cardsByStore.cards}
                showCards={showCards}
              />
            ))}
          </>
        )}
        {!loading && cardsNotFound.length > 0 && (
          <CardsNotFoundList cardsNotFound={cardsNotFound} />
        )}
        {!loading && totalCards !== null && (
          <p className="text-white text-sm">
            {formatter.formatNumber(totalCards)}{' '}
            {totalCards === 1 ? 'resultado' : 'resultados'}
          </p>
        )}
        {!loading && hasMore && (
          <Button iconClass="fas fa-chevron-down" onClick={loadMore}>
            Cargar más resultados
          </Button>
        )}
      </div>
    </div>
  );
};

export default FindCard;
